export interface SSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  children?: SSelectOption[];
}

export type SSelectType = 'default' | 'multi' | 'default_depth' | 'multi_depth';

export interface FlatOption {
  value: string; // 내부적으로 문자열로 정규화
  label: string;
  disabled: boolean;
  depth: number;
  /** children 을 가진 그룹(비선택) 노드 */
  isGroup: boolean;
  raw: string | number;
}

/** 트리 옵션을 depth 정보를 유지한 평탄 리스트로 변환. */
export function flattenOptions(options: SSelectOption[], depth = 1): FlatOption[] {
  const out: FlatOption[] = [];
  for (const o of options) {
    const hasChildren = Array.isArray(o.children) && o.children.length > 0;
    if (hasChildren) {
      const childFlat = flattenOptions(o.children!, depth + 1);
      // 그룹(depth2/3 포함): 자기 자신이 disabled 이거나, 하위 항목이 전부 disabled 이면
      // 어미도 disabled 로 취급한다. (childFlat 은 하위 그룹의 계산 결과를 이미 포함하므로 재귀적으로 성립)
      const allChildrenDisabled = childFlat.every(c => c.disabled);
      out.push({
        value: String(o.value),
        label: o.label,
        disabled: !!o.disabled || allChildrenDisabled,
        depth,
        isGroup: true,
        raw: o.value,
      });
      out.push(...childFlat);
    } else {
      out.push({
        value: String(o.value),
        label: o.label,
        disabled: !!o.disabled,
        depth,
        isGroup: false,
        raw: o.value,
      });
    }
  }
  return out;
}

/** 선택 가능한(비그룹) 옵션만 */
export const selectableOptions = (flat: FlatOption[]) =>
  flat.filter(o => !o.isGroup && !o.disabled);

/**
 * flat 배열에서 groupIndex 위치 그룹의 하위(자손) 리프 중 '토글 가능한'(비활성 아님)
 * 것들의 정규화 value 목록. flat 은 DFS 순서라 그룹 뒤로 depth 가 더 깊은 연속 구간이
 * 그 그룹의 자손이며, 중첩 그룹의 리프까지 재귀 없이 모두 포함된다.
 * (multi_depth 부모 체크박스의 상태/카운트/토글 계산에 사용)
 */
export function collectGroupLeafValues(flat: FlatOption[], groupIndex: number): string[] {
  const group = flat[groupIndex];
  if (group == null || !group.isGroup) return [];
  const values: string[] = [];
  for (let i = groupIndex + 1; i < flat.length; i++) {
    if (flat[i].depth <= group.depth) break;
    if (!flat[i].isGroup && !flat[i].disabled) values.push(flat[i].value);
  }
  return values;
}

/** 라벨에 HTML 태그가 있는지 (원본 isHtmlLabel) */
export const isHtmlLabel = (label: string): boolean => /<[a-z][\s\S]*>/i.test(label);

/** HTML 라벨에서 태그를 제거한 텍스트 (검색 매칭용, 원본 extractText) */
export const extractText = (html: string): string => html.replace(/<[^>]*>/g, '');
